<script setup>
import { Learner } from '@pfsc/core/models/learner';
import { getUserReadableStatus, User } from '@pfsc/core/models/user';
import { useAuthStore } from '@pfsc/core/stores/auth.store';
import { redirectUtils } from '@pfsc/core/utils/redirect';
import { ref, watch } from 'vue';
import { globalTranslate } from '../locale/app.i18n';
import UnassignedTableCheckbox from './unassigned-table-checkbox.vue';
import { computed } from 'vue';

// When show-select is true, the table will have this ID for the select checkbox row.
// However, if we use a custom #header to render the table, the select all rows checkbox will also be hidden
// Therefore, we need to manually handle selecting all rows again.
const SELECT_ROW_COLUMN_KEY = 'data-table-select';

/**
 * Vuetify header sizes can be number (px) or string (css).
 * @param {number | string | null | undefined} v Value.
 * @returns {string | undefined}
 */
function toCssSize(v) {
  if (v == null) {
    return undefined;
  }
  return typeof v === 'number' ? `${v}px` : v;
}

function rawLearner(item) {
  return /** @type {Learner} */ (item.raw);
}

const props = defineProps({
  items: {
    type: Array,
    required: true,
  },

  isLoading: {
    type: Boolean,
    default: false,
  },

  modelValue: {
    type: Array,
    required: false,
    default: () => [],
  },
  headers: {
    /** @type {import("vue").PropType<Array<{ title: string, key: string }>>} */
    type: /** @type {import("vue").PropType<Array<{ title: string, key: string }>>} */ (Array),
    required: true,
  },
  maximumLearnersSelected: {
    type: Number,
    default: null,
  },

  actionButtonTitle: {
    type: String,
    default: '',
  },

  hasStudentViewUrl: {
    type: Boolean,
    default: false,
  },

  showSelect: {
    type: Boolean,
    default: true,
  },

  shouldApplyClass: {
    type: Boolean,
    default: true,
  },

  selectStrategy: {
    /** @type {import("vue").PropType<"all" | "single" | "page">} */
    type: String,
    required: false,
    default: undefined,
  },
  mobileCellColumn: {
    type: /** @type {import("vue").PropType<{ key: string, sortable: boolean, title: string }>} */ (Object),
    default: () => ({ key: 'id', sortable: false, title: globalTranslate('assignment.learnerInfo') }),
  },

  disableHiddenLearners: {
    type: Boolean,
    default: false,
  },
});

const emits = defineEmits(['on-remove', 'on-action', 'update:model-value']);

const authStore = useAuthStore();

const isSelectAllRow = ref(false);

const tableHeaders = computed(() => [
  {
    title: globalTranslate('assignment.learnerInfo'),
    key: props.mobileCellColumn.key,
    sortable: props.mobileCellColumn.sortable,
    minWidth: '280px',
  },
]);

/**
 * Check if learner list include another list.
 * @param {Array} l1 Learner list 1.
 * @param {Array} l2 Learner list 2.
 * @returns {boolean}
 */
function checkIfLearnerListIncludeAnotherList(l1, l2) {
  const l1Ids = l1.map(l => l.id);
  const l2Ids = l2.map(c => c.id);

  return l2Ids.every(item => l1Ids.includes(item));
}

/**
 * Filter selectable item not included in a list.
 * @param {Learner[]} allItems All learners.
 * @param {Array<any>} exclude List to exclude.
 * @returns {Learner[]}
 */
function filterItemNotIncludedInAList(allItems, exclude) {
  const excludeIds = new Set(exclude.map(item => item.id));
  return allItems.filter(item => (
    (!props.disableHiddenLearners || item.isVisible)
    && !excludeIds.has(item.id)
  ));
}

/**
 * Toggle state of select all checkbox.
 * @param {Array} selectedRows Selected rows.
 */
function toggleSelectAllCheckbox(selectedRows) {
  const isIncludesAll = checkIfLearnerListIncludeAnotherList(selectedRows, props.items)
    || selectedRows.length === props.maximumLearnersSelected;

  if (isIncludesAll) {
    isSelectAllRow.value = true;
    return;
  }
  isSelectAllRow.value = false;
}

/**
 * Handle select all row.
 * @param {(value: boolean) => void} selectAll Select all callback.
 * @param {boolean} allSelected Whether all currently selectable rows are already selected.
 */
function onSelectAllRow(selectAll, allSelected) {
  const willSelectAll = !allSelected;
  if (!willSelectAll || props.maximumLearnersSelected == null) {
    selectAll(willSelectAll);
    return;
  }
  const unselectedLearners = filterItemNotIncludedInAList(props.items, props.modelValue);
  const remainingCapacity = props.maximumLearnersSelected - props.modelValue.length;
  const newSelectedLearners = [
    ...props.modelValue,
    ...unselectedLearners.slice(0, Math.max(remainingCapacity, 0)),
  ];
  emits('update:model-value', newSelectedLearners);
  toggleSelectAllCheckbox(newSelectedLearners);
}

/**
 * Remove learner.
 * @param {Learner} learner Learner.
 */
function onRemoveClick(learner) {
  emits('on-remove', learner);
}

/**
 * Handle action click
 * @param {Learner} learner Learner.
 */
async function onActionClick(learner) {
  emits('on-action', learner);
}

/**
 * Cast slot raw item to learner.
 * @param {unknown} value Slot item raw.
 * @returns {Learner}
 */
function toLearner(value) {
  return /** @type {Learner} */ (value);
}

/**
 * Cast slot raw item to user.
 * @param {unknown} value Slot item raw.
 * @returns {User}
 */
function toUser(value) {
  return /** @type {User} */ (value);
}

/**
 * On select learners
 * @param {Learner[]} learners Learners
 */
function onSelectRow(learners) {
  emits('update:model-value', learners);
  toggleSelectAllCheckbox(learners);
}

/**
 * Is learner selectable.
 * @param {Learner} learner Learner.
 * @returns {boolean}
 */
function isItemSelectable(learner) {
  if (props.disableHiddenLearners && !learner.isVisible) {
    return false;
  }
  const isLearnerSelected = props.modelValue.find(
    /**
     * @param {import('@pfsc/core/models/learner').Learner} l Row in current selection.
     * @returns {boolean}
     */
    l => l.id === learner.id,
  ) != null;
  if (isLearnerSelected) {
    return true;
  }
  if (props.maximumLearnersSelected == null) {
    return true;
  }
  return props.maximumLearnersSelected > props.modelValue.length;
}

watch(() => props.items, () => {
  const isIncludesAll = checkIfLearnerListIncludeAnotherList(props.modelValue, props.items);
  if (isIncludesAll) {
    isSelectAllRow.value = true;
    return;
  }

  isSelectAllRow.value = false;
});

</script>

<template>
  <!-- Use v-data-table-server because scrolling is laggy when using v-data-table-virtual -->
  <v-data-table-server
    :model-value="props.modelValue"
    :headers="tableHeaders"
    :items="items"
    :loading="isLoading"
    return-object
    :item-selectable="isItemSelectable"
    :items-length="items.length"
    :select-strategy="selectStrategy"
    :show-select="showSelect"
    fixed-header
    sort-asc-icon="fas fa-sort-up"
    sort-desc-icon="fas fa-sort-down"
    :no-data-text="globalTranslate('table.noDataText')"
    :class="['app-table', props.shouldApplyClass ? $style.unassignedLearnerTable : undefined]"
    @update:model-value="onSelectRow"
  >
    <template
      #headers="{ columns, isSorted, getSortIcon, toggleSort, someSelected, allSelected, selectAll, sortBy }"
    >
      <tr>
        <td v-if="showSelect" :class="[$style.checkboxHeader, $style.header]">
          <v-checkbox-btn
            v-if="selectStrategy !== 'single'"
            v-model="isSelectAllRow"
            :density="'compact'"
            :indeterminate="someSelected && !allSelected"
            @click="onSelectAllRow(selectAll, allSelected)"
          />
        </td>
        <template v-for="column in columns" :key="column.key">
          <td
            v-if="column.key !== SELECT_ROW_COLUMN_KEY"
            :class="$style.header"
            :style="{
              width: toCssSize(column.width),
              minWidth: toCssSize(column.minWidth),
              maxWidth: toCssSize(column.maxWidth),
            }"
          >
            <div :class="[column.sortable && $style.pointerCursor]">
              <span
                :style="{ textAlign: column.align ?? 'left' , cursor: column.sortable ? 'pointer' : 'default' }"
                @click="
                  () => {
                    if (column.sortable) {
                      toggleSort(column);
                    }
                  }
                "
              >{{ column.title }}
                <v-icon
                  v-if="isSorted(column)"
                  :class="$style.sortIcon"
                  :size="16"
                  :icon="getSortIcon(column)"
                />
                <v-icon
                  v-if="column.sortable && sortBy.length === 0"
                  :class="$style.sortIcon"
                  :size="16"
                  icon="fas fa-sort"
                />
              </span>
            </div>
          </td>
        </template>
      </tr>
    </template>

    <template #[`item.${props.mobileCellColumn.key}`]="{ internalItem: item }">
      <slot :name="`item-cell.${props.mobileCellColumn.key}`" :item="item">
        <div :class="$style.mobileCell">
          <template v-for="column in headers" :key="column.key">
            <slot
              v-if="column.key === 'id'"
              :name="`item.${column.key}`"
              :item="item"
              :column="column"
            >
              <div :class="$style.mobileLine">
                <span :class="$style.mobileLabel">{{ globalTranslate('header.userId') }}:</span>
                <span v-if="hasStudentViewUrl">
                  <router-link class="link" :to="redirectUtils.getStudentDetailUrl(authStore.currentUser, rawLearner(item), '')">
                    {{ rawLearner(item).id }}
                  </router-link>
                </span>
                <span v-else>
                  {{ rawLearner(item).id }}
                </span>
              </div>
            </slot>
            <slot
              v-else-if="column.key === 'status'"
              :name="`item.${column.key}`"
              :item="item"
              :column="column"
            >
              <div :class="$style.mobileLine">
                <span :class="$style.mobileLabel">{{ column.title }}:</span>
                {{ getUserReadableStatus(toUser(item.raw)) }}
              </div>
            </slot>
            <slot
              v-else-if="column.key === 'delete'"
              :name="`item.${column.key}`"
              :item="item"
              :column="column"
            >
              <div :class="[$style.mobileLine, 'justify-content-end']">
                <button
                  :title="globalTranslate('unassignedLearnersTable.deleteButton')"
                  type="button"
                  @click="onRemoveClick(toLearner(item.raw))"
                >
                  <v-icon size="small" icon="fas fa-solid fa-trash v-icon_secondary" />
                </button>
              </div>
            </slot>
            <slot
              v-else-if="column.key === 'assign'"
              :name="`item.${column.key}`"
              :item="item"
              :column="column"
            >
              <div :class="$style.mobileLine">
                <button
                  :class="['text', $style.actionButton]"
                  type="button"
                  @click="onActionClick(toLearner(item.raw))"
                >
                  {{ globalTranslate('assignment.assign') }}
                </button>
              </div>
            </slot>
            <slot
              v-else-if="column.key === 'action'"
              :name="`item.${column.key}`"
              :item="item"
              :column="column"
            >
              <div :class="$style.mobileLine">
                <button
                  :class="['text', $style.actionButton]"
                  type="button"
                  @click="onActionClick(toLearner(item.raw))"
                >
                  {{ actionButtonTitle }}
                </button>
              </div>
            </slot>
            <div v-else :class="$style.mobileLine">
              <span :class="$style.mobileLabel">{{ column.title }}:</span>
              <slot
                :name="`item.${column.key}`"
                :item="item"
                :column="column"
              >
                <span>{{ item.raw[column.key] }}</span>
              </slot>
            </div>
          </template>
        </div>
      </slot>
    </template>

    <template #[`item.data-table-select`]="{ internalItem, item, toggleSelect, isSelected }">
      <unassigned-table-checkbox
        :disabled="!internalItem.selectable"
        :model-value="isSelected({value: item, selectable: internalItem.selectable})"
        @update:model-value="($event) => toggleSelect({value: item, selectable: $event})"
        @click.stop
      />
    </template>

    <!-- To remove default pagination footer -->
    <template #bottom />
  </v-data-table-server>
</template>

<style module lang="scss">

.checkboxHeader {

  /* To overwrite default padding of vue table header. */
  padding-left: 8px !important
}

.header {
  border-bottom: 1px solid rgba(var(--v-border-color), var(--v-border-opacity));
  width: fit-content;
  background: white;
  font-weight: 600;
}

.pointerCursor {
  cursor: pointer;
}

.sortIcon {
  margin-bottom: 4px;
}

.unassignedLearnerTable {
    height: calc(100vh - 505px);
}

.mobileCell {
  display: flex;
  flex-direction: column;
  gap: 6px;
  padding: 8px 0;
  font-size: 14px;
}

.mobileLine {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  align-items: center;
}

.mobileLabel {
  font-weight: 600;
  width: 125px;
}

@media screen and (max-width: 1366px) {
  .unassignedLearnerTable {
    height: calc(100vh - 565px);
  }
}

@media screen and (max-width: 600px) {
  .unassignedLearnerTable {
    min-height: 375px;
  }
}

@media screen and (max-height: 650px) {
  .unassignedLearnerTable {
    min-height: 375px;
  }
}


.actionButtonRow {
  display: flex;
  gap: 8px;
  margin-top: 4px;
}

.actionButton {
  border: 1px solid var(--primary-color) !important;
}

</style>
